import { Location } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import {
	UntypedFormBuilder,
	UntypedFormGroup,
	Validators,
} from '@angular/forms';
import { ActivatedRoute } from '@angular/router';

import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { SnackBarService } from 'src/app/shared/components/snack-bar/snack-bar.service';

import { AddressConstants } from '../../../../../modules/address/address.constants';
import {
	createPartnerBrokerStart,
	createPartnerBrokerSuccess,
	editPartnerBrokerRegStart,
	editPartnerBrokerRegSuccess,
	fetchCnpjAvailabilityStart,
	fetchPartnerBrokerRegStart,
	fetchUFCitiesStart,
	restoreFormReset,
} from '../../../state/organizations.actions';
import {
	selectCities,
	selectCnpjAvailability,
	selectRestoreForm,
	selectPartnerBroker,
} from '../../../state/organizations.selector';

@Component({
	selector: 'app-partner-broker-registration',
	styleUrls: ['./partner-broker-registration.component.scss'],
	templateUrl: './partner-broker-registration.component.html',
})
export class PartnerBrokerRegistrationComponent implements OnInit, OnDestroy {
	auxStateForm!: boolean;
	cities: string[] = [];
	cnpjPattern = '[0-9]{14}';
	form: UntypedFormGroup;
	inputId: string = String(Math.random());
	loading: boolean = false;

	orgId: string = '';
	partnerId: string = '';
	uf = AddressConstants.ufLabels;

	private submittedName: string | null = null;
	private readonly unsubscribe$: Subject<void> = new Subject();

	constructor(
		private readonly fb: UntypedFormBuilder,
		private readonly activatedRoute: ActivatedRoute,
		private readonly location: Location,
		private readonly store: Store,
		private readonly actions$: Actions,
		private readonly snackBarService: SnackBarService,
	) {
		this.form = this.fb.group({
			active: [true],
			city: [{ disabled: true, value: null }],
			cnpj: [
				null,
				[
					Validators.required,
					Validators.minLength(14),
					Validators.maxLength(14),
				],
			],
			municipal_registration: [null],
			name: [null, [Validators.required]],
			name_secondary: [null, [Validators.required]],
			state: [null],
			state_registration: [null],
		});
	}

	ngOnDestroy(): void {
		this.unsubscribe$.next();
		this.unsubscribe$.complete();
		this.store.dispatch(restoreFormReset());
	}

	ngOnInit(): void {
		this.activatedRoute.params.subscribe(({ id, pId }) => {
			if (!id || !pId) {
				this.auxStateForm = true;
				this.loading = false;
			} else {
				this.orgId = id;
				this.partnerId = pId;
				this.store.dispatch(fetchPartnerBrokerRegStart({ id: pId }));
				this.store
					.select(selectPartnerBroker)
					.pipe(takeUntil(this.unsubscribe$))
					.subscribe((p: any) => {
						if (!p) {
							return;
						}

						const city = (p as any).city ?? null;
						const cnpj = (p as any).cnpj ?? null;
						const municipal_registration =
							(p as any).municipal_registration ?? null;
						const name = (p as any).name ?? null;
						const name_secondary = (p as any).name_secondary ?? null;
						const state = (p as any).state ?? null;
						const state_registration = (p as any).state_registration ?? null;
						const active = (p as any).active ?? true;

						this.form = this.fb.group({
							active: [active],
							city: [{ disabled: true, value: city }],
							cnpj: [
								{ disabled: true, value: cnpj },
								[
									Validators.required,
									Validators.minLength(14),
									Validators.maxLength(14),
								],
							],
							municipal_registration: [municipal_registration],
							name: [name, [Validators.required]],
							name_secondary: [name_secondary, [Validators.required]],
							state: [state],
							state_registration: [state_registration],
						});
						this.auxStateForm = false;
						this.loading = false;
					});
			}
		});
		this.store
			.select(selectCities)
			.pipe(takeUntil(this.unsubscribe$))
			.subscribe((val) => {
				const cities = (val as string[]) ?? [];

				if (Array.isArray(cities) && cities.length) {
					this.cities = cities;
					this.form.get('city')!.enable();
				}
			});
		this.listenForSubmitSuccess();

		this.store
			.select(selectRestoreForm)
			.pipe(takeUntil(this.unsubscribe$))
			.subscribe((restore) => {
				if (restore) {
					this.loading = false;
				}
			});
	}

	onCancel(): void {
		this.location.back();
	}

	onCnpjBlur(): void {
		if (this.partnerId) return;

		const cnpjControl = this.form.get('cnpj');
		const raw = cnpjControl?.value ?? '';
		const digitsOnly = String(raw).replace(/[^\d]/g, '');

		if (cnpjControl?.valid && digitsOnly.length === 14) {
			this.store.dispatch(
				fetchCnpjAvailabilityStart({
					category: 'partner-broker',
					cnpj: digitsOnly,
				}),
			);
			this.store
				.select(selectCnpjAvailability)
				.pipe(takeUntil(this.unsubscribe$))
				.subscribe((isAvailable) => {
					if (isAvailable === false) {
						cnpjControl.setErrors({ notAvailable: true });
					}
				});
		}
	}

	onSubmit(): void {
		if (this.form.invalid) {
			this.updateValidation();

			return;
		}

		this.loading = true;

		const wasCnpjDisabled = this.form.get('cnpj')!.disabled;
		if (wasCnpjDisabled) this.form.get('cnpj')!.enable();

		const rawValue = this.form.getRawValue();
		this.submittedName = rawValue?.name ?? null;
		const request = {
			...rawValue,
			cnpj: String(rawValue.cnpj ?? '').replace(/[^\d]/g, ''),
		};

		if (wasCnpjDisabled) this.form.get('cnpj')!.disable();

		if (this.partnerId) {
			this.store.dispatch(
				editPartnerBrokerRegStart({ id: this.partnerId, request }),
			);
		} else {
			this.store.dispatch(createPartnerBrokerStart({ request }));
		}
	}

	onUFChange(id: number): void {
		this.form.get('city')!.setValue(null);
		this.form.get('city')!.disable();
		this.auxStateForm = true;
		if (id !== -1) {
			this.store.dispatch(fetchUFCitiesStart({ id }));
		}
	}

	shouldDisableSaveBtn(): boolean {
		return this.form.invalid || this.form.untouched;
	}

	private listenForSubmitSuccess(): void {
		this.actions$
			.pipe(
				ofType(createPartnerBrokerSuccess, editPartnerBrokerRegSuccess),
				takeUntil(this.unsubscribe$),
			)
			.subscribe((action) => {
				const verb =
					(action as any).type.includes('edit') ||
					(action as any).type.includes('Edit') ||
					(action as any).type.includes('update')
						? 'atualizada'
						: 'criada';

				let name = this.submittedName;

				if (!name || name.trim() === '') {
					const current = this.form?.getRawValue?.();
					name = current?.name ?? '';
				}

				const message =
					name && String(name).trim() !== ''
						? `Corretora "${name}" ${verb} com sucesso.`
						: `Corretora ${verb} com sucesso.`;

				this.snackBarService.showSnack(message, 'success');
			});
	}

	private updateValidation(): void {
		for (const controlName of Object.keys(this.form.controls)) {
			const control = this.form.get(controlName)!;
			control.markAsTouched();
			control.updateValueAndValidity({ onlySelf: true });
		}
	}
}
